<template>
  <div class="paysquare-box">
    <!-- Lanzador con imagen -->
    <v-btn
      v-if="isUSA"
      :block="block"
      depressed
      color="transparent"
      class="pc-launcher"
      :disabled="loading"
      @click="open"
    >
      <v-img
        :src="buttonImageUrl"
        contain
        :max-height="buttonMaxHeight"
        :max-width="buttonMaxWidth"
        class="mx-auto"
      />
    </v-btn>

    <!-- Popup -->
    <v-dialog
      v-model="dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :max-width="maxWidth"
      scrollable
      :persistent="persistent"
      :overlay-color="'#0b1220'"
      :overlay-opacity="0.75"
      content-class="pc-dialog"
      @input="v => { if (!v) $emit('closed') }"
    >
      <v-card class="pc-card">
        <!-- Header -->
        <div class="pc-header">
          <div class="pc-chip">
            <v-icon size="18" class="mr-1">mdi-bitcoin</v-icon>
            Recarga Automatica
          </div>
          <h3 class="pc-title">{{ title }}</h3>
          <v-btn icon class="pc-close" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Cuerpo -->
        <v-card-text class="pc-body">
          <!-- Error -->
          <v-alert v-if="errorMsg" type="error" dense outlined class="mb-3">
            {{ errorMsg }}
          </v-alert>

          <!-- Paso 1: formulario (SOLO si NO está bloqueado) -->
          <v-form
            v-if="step === 1 && !blocked"
            ref="form"
            v-model="valid"
            lazy-validation
            class="pc-form"
          >
            <div class="pc-amount">
              <label class="pc-amount__label">Monto a depositar</label>
              <v-text-field
                v-model.number="amount"
                type="number"
                min="40"
                step="0.01"
                prefix="$"
                :rules="[
                  v => (v !== '' && v !== null && v !== undefined) || 'Requerido',
                  v => Number(v) >= 40 || 'El monto mínimo es 40',
                ]"
                hide-details="auto"
                outlined
                dense
                class="pc-amount__input"
              />
              <small class="pc-amount__hint">Monto minimo $40.00</small>

              <!-- INFO DE GASTOS + TOTAL (SIN CAMBIAR EL FORM) -->
              <div class="pc-fee">
                <small class="pc-fee__line">
                  Gastos de plataforma (1,49%):
                  <strong>${{ Number(feeAmountRounded).toFixed(2) }}</strong>
                </small>
                <small class="pc-fee__line pc-fee__total">
                  Total a pagar:
                  <strong>${{ Number(totalAmountRounded).toFixed(2) }}</strong>
                </small>
              </div>
            </div>

            <v-btn
              class="pc-cta"
              :loading="loading"
              :disabled="!valid || loading"
              @click="startCheckout"
            >
              <v-icon left>mdi-lock</v-icon> Generar enlace de pago
            </v-btn>
          </v-form>

          <!-- Paso 2: creando orden (solo si no está bloqueado) -->
          <div
            v-else-if="!blocked && loading && step === 2 && !payUrl && !payHtml"
            class="pc-loading"
          >
            <div class="pc-spinner"></div>
            Creando orden cripto…
          </div>

          <!-- Paso 2: tenemos URL -->
          <div v-if="!blocked && step === 2 && payUrl" class="pc-iframe-wrap">
            <div class="pc-loading pc-loading--center" style="padding:16px;">
              <div class="pc-spinner"></div>
              <span class="ml-2">
                Hemos generado tu link de pago de Square.
                Si la ventana no se abrió automáticamente, usa los botones:
              </span>
            </div>

            <div class="pc-actions-inline">
              <v-btn text small class="mr-2" @click="openPopup">
                <v-icon left small>mdi-open-in-new</v-icon>
                Abrir ventana
              </v-btn>

              <v-btn text small class="mr-2" :href="payUrl" target="_blank" rel="noopener">
                <v-icon left small>mdi-open-in-new</v-icon>
                Abrir en nueva pestaña
              </v-btn>

              <v-btn text small @click="copyUrlOrHtml">
                <v-icon left small>mdi-content-copy</v-icon>
                Copiar URL
              </v-btn>
            </div>
          </div>

          <!-- Paso 2: modo HTML -->
          <div v-else-if="!blocked && step === 2 && payHtml" class="pc-iframe-wrap">
            <div class="pc-loading pc-loading--center" style="padding:16px;">
              <div class="pc-spinner"></div>
              <span class="ml-2">
                Hemos generado el checkout del proveedor.
                Ábrelo en una ventana para continuar.
              </span>
            </div>

            <div class="pc-actions-inline">
              <v-btn text small class="mr-2" @click="openPopup">
                <v-icon left small>mdi-open-in-new</v-icon>
                Abrir ventana
              </v-btn>

              <v-btn
                v-if="htmlBlobUrl"
                text
                small
                class="mr-2"
                :href="htmlBlobUrl"
                target="_blank"
                rel="noopener"
              >
                <v-icon left small>mdi-open-in-new</v-icon>
                Abrir en nueva pestaña
              </v-btn>

              <v-btn text small @click="copyUrlOrHtml">
                <v-icon left small>mdi-content-copy</v-icon>
                Copiar HTML
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <!-- Footer -->
        <v-card-actions class="pc-actions">
          <v-spacer />
          <v-btn
            v-if="!blocked && !loading && !payUrl && !payHtml"
            small
            outlined
            class="mr-2"
            @click="startCheckout"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Reintentar
          </v-btn>
          <v-btn text class="pc-secondary" @click="close">Cerrar</v-btn>
        </v-card-actions>

        <!-- 🔒 GATE OVERLAY -->
        <div v-if="showGate" class="pp-gate">
          <div class="pp-gate__box">
            <v-icon size="28" class="mr-2">mdi-shield-lock</v-icon>
            <div class="pp-gate__title">Debes estar verificado</div>
            <div class="pp-gate__msg">
              Debes estar verificado para usar este método de recarga.
            </div>
            <div class="pp-gate__actions">
              <v-btn text class="pp-gate__btn--secondary" @click="onGateClose">Cerrar</v-btn>
              <v-btn class="pp-gate__btn--primary" @click="onGateVerify">
                <v-icon left>mdi-account-check</v-icon>
                Verifícate ahora
              </v-btn>
            </div>
          </div>
        </div>
        <!-- /GATE -->
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Paycripto',
  props: {
    // Backend
    basePath: { type: String, default: '' },
    endpointStart: { type: String, default: '/paysquare/start' },

    // Defaults / Catálogos
    defaultAmount: { type: [Number, String], default: '' },
    defaultCrypto: { type: String, default: 'USDT' },
    defaultNetwork: { type: String, default: 'TRC20' },
    cryptos: { type: Array, default: () => ['USDT', 'USDC', 'BTC', 'ETH'] },
    networks: { type: Array, default: () => ['TRC20', 'ERC20', 'BEP20', 'Polygon'] },
    showNetwork: { type: Boolean, default: true },

    // Dónde buscar la URL de pago en JSON
    responseUrlFields: {
      type: Array,
      default: () => ['iframeUrl', 'checkoutUrl', 'payUrl', 'redirectUrl', 'url', 'data.url'],
    },

    // UI
    title: { type: String, default: 'Recargar con cripto' },
    block: { type: Boolean, default: true },
    maxWidth: { type: [Number, String], default: 680 },
    persistent: { type: Boolean, default: false },
    buttonImageUrl: {
      type: String,
      default: 'https://trackandraces.com/cuentas/pagoexpress.png',
    },
    buttonMaxWidth: { type: [Number, String], default: 220 },
    buttonMaxHeight: { type: [Number, String], default: 90 },

    // postMessage
    postMessageDoneType: { type: String, default: 'CRYPTO_TOPUP_DONE' },
    postMessageErrorType: { type: String, default: 'CRYPTO_TOPUP_ERROR' },

    // 🔒 Estado de verificación (si lo pasas desde el padre)
    isUserVerified: { type: Boolean, default: false },

    // 🔗 Ruta para verificar
    verifyRoute: { type: String, default: '/cuenta' },
  },
  data() {
    return {
      dialog: false,
      valid: false,
      step: 0, // 0=nada/gate, 1=form, 2=checkout
      loading: false,
      amount: '',
      crypto: '',
      network: '',
      isVerified: false,
      isApproved: false,
      blocked: false,

      payUrl: '',
      payHtml: '',
      htmlBlobUrl: '',

      orderId: null,
      clientTxId: null,
      errorMsg: '',

      isUSA: false,
    };
  },
  computed: {
    showGate() {
      return this.dialog && this.blocked;
    },

    // ===== 1.49% fee + total =====
    feeRate() {
      return 0.0149; // 1.49%
    },
    feeAmount() {
      const a = Number(this.amount || 0);
      if (!isFinite(a) || a <= 0) return 0;
      return a * this.feeRate;
    },
    feeAmountRounded() {
      // Redondeo a 2 decimales para mostrar
      return Math.round(this.feeAmount * 100) / 100;
    },
    totalAmount() {
      const a = Number(this.amount || 0);
      if (!isFinite(a) || a <= 0) return 0;
      return a + this.feeAmount;
    },
    totalAmountRounded() {
      // Redondeo a 2 decimales para cobrar/enviar
      return Math.round(this.totalAmount * 100) / 100;
    },
  },
  mounted() {
    this.checkVerificationStatus();
    this.amount = this.defaultAmount;
    this.crypto = this.defaultCrypto;
    this.network = this.defaultNetwork;
    this.detectUSA();
    window.addEventListener('message', this.onMessage);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onMessage);
    this.revokeBlob();
  },
  methods: {
    async checkVerificationStatus() {
      try {
        const response = await axios.get('/verification/status');
        const verified = response && response.data ? response.data.verified : false;
        const approved = response && response.data ? response.data.approved : false;
        this.isVerified = !!verified;
        this.isApproved = !!approved;
      } catch (error) {
        console.error('Error checking verification status:', error);
        this.isVerified = false;
        this.isApproved = false;
      }
    },

    async detectUSA() {
      try {
        const resp = await axios.get('https://ipapi.co/json/');
        const countryCode = resp && resp.data ? resp.data.country_code : null;
        this.isUSA = countryCode === 'US';
      } catch (err) {
        console.error('Error detectando país', err);
        this.isUSA = false;
      }
    },

    reset() {
      this.loading = false;
      this.payUrl = '';
      this.payHtml = '';
      this.revokeBlob();
      this.errorMsg = '';
      this.orderId = null;
      this.clientTxId = null;
      this.valid = false;
    },

    open() {
      this.dialog = true;
      this.reset();
      this.step = 0;

      // decide verificación SIN mutar el prop
      const verifiedNow = (this.isVerified && this.isApproved) || this.isUserVerified;

      this.blocked = !verifiedNow;
      if (!this.blocked) this.step = 1;
    },

    onGateClose() {
      this.blocked = false;
      this.close();
    },

    onGateVerify() {
      if (this.$router) this.$router.push(this.verifyRoute);
      this.blocked = false;
      this.close();
    },

    close() {
      this.dialog = false;
      this.reset();
      this.step = 0;
      this.blocked = false;
    },

    revokeBlob() {
      if (this.htmlBlobUrl) {
        URL.revokeObjectURL(this.htmlBlobUrl);
        this.htmlBlobUrl = '';
      }
    },

    buildClientTxId() {
      var ts = Date.now();
      var rnd = Math.random().toString(36).slice(2, 8);
      return 'PPC-' + ts + '-' + rnd;
    },

    readPath(obj, path) {
      try {
        var parts = String(path).split('.');
        var acc = obj;
        for (var i = 0; i < parts.length; i++) {
          if (!acc) return null;
          acc = acc[parts[i]];
        }
        return acc || null;
      } catch (err) {
        console.error('readPath error', err);
        return null;
      }
    },

    makeHtmlBlob() {
      try {
        this.revokeBlob();
        var blob = new Blob([this.payHtml], { type: 'text/html' });
        this.htmlBlobUrl = URL.createObjectURL(blob);
      } catch (err) {
        this.htmlBlobUrl = '';
        console.error('makeHtmlBlob failed', err);
      }
    },

    async startCheckout() {
      if (this.blocked) return;
      if (!this.$refs.form) return;

      var ok = this.$refs.form.validate();
      if (!ok || this.loading) return;

      this.loading = true;
      this.errorMsg = '';
      this.payUrl = '';
      this.payHtml = '';
      this.revokeBlob();
      this.step = 2;

      try {
        this.clientTxId = this.buildClientTxId();

        var payload = {
          clientTxId: this.clientTxId,
          // ✅ Enviar el monto + 1.49%
          amount: Number(this.totalAmountRounded),
          crypto: this.crypto,
        };
        if (this.showNetwork) payload.network = this.network;

        var resp = await axios.post(
          '' + this.basePath + this.endpointStart,
          payload,
          {
            headers: { Accept: 'text/html,application/json' },
            responseType: 'text',
            transformResponse: [function(v) { return v; }],
          }
        );

        var rawText = (resp && typeof resp.data === 'string') ? resp.data : String(resp.data);

        // Intentar JSON primero
        var json = null;
        try {
          json = JSON.parse(rawText);
        } catch (err) {
          json = null;
        }

        // si el backend devuelve ok:false
        if (json && json.ok === false) {
          throw new Error(json.message || 'Error del backend');
        }

        if (json && typeof json === 'object') {
          this.orderId = json.orderId || json.ordenId || json.id || null;

          var url = null;
          for (var i = 0; i < this.responseUrlFields.length; i++) {
            url = this.readPath(json, this.responseUrlFields[i]);
            if (url) break;
          }

          if (!url) {
            var raw = json.raw || {};
            url = raw.url || raw.payment_url || null;
          }

          if (!url) throw new Error('El backend no devolvió URL ni HTML.');
          this.payUrl = url;
        } else {
          // No era JSON -> asumimos HTML del gateway
          this.orderId = null;
          this.payHtml = rawText;
          this.makeHtmlBlob();
        }

        this.$emit('ready', {
          orderId: this.orderId,
          clientTxId: this.clientTxId,
          url: this.payUrl || null,
          raw: json ? json : rawText,
          mode: this.payUrl ? 'url' : 'html',
        });

        // Abrir checkout
        this.openPopup();
      } catch (e) {
        this.errorMsg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.response && e.response.data) ||
          (e && e.message) ||
          'Error iniciando pago cripto';
        this.$emit('error', this.errorMsg);
        this.step = this.blocked ? 0 : 1;
      } finally {
        this.loading = false;
      }
    },

    onMessage(e) {
      var msg = e && e.data ? e.data : {};
      if (msg.type === this.postMessageDoneType) {
        this.$emit('success', msg.payload || {});
        this.close();
      } else if (msg.type === this.postMessageErrorType) {
        this.$emit('error', msg.payload || 'Error en confirmación');
        this.close();
      }
    },

    async copyUrlOrHtml() {
      try {
        if (this.payUrl) {
          await navigator.clipboard.writeText(this.payUrl);
          this.$emit('copied', 'url');
        } else if (this.payHtml) {
          await navigator.clipboard.writeText(this.payHtml);
          this.$emit('copied', 'html');
        } else {
          this.$emit('error', 'Nada para copiar');
        }
      } catch (err) {
        this.$emit('error', 'No se pudo copiar al portapapeles');
        console.error('copyUrlOrHtml failed', err);
      }
    },

    openPopup() {
      var features = [
        'toolbar=no',
        'location=no',
        'status=no',
        'menubar=no',
        'scrollbars=yes',
        'resizable=yes',
        'width=480',
        'height=720',
        'top=' + (window.screenY + 40),
        'left=' + (window.screenX + 40),
      ].join(',');

      // URL directa
      if (this.payUrl) {
        var winUrl = window.open(this.payUrl, 'paysquare_popup', features);
        if (!winUrl || winUrl.closed || typeof winUrl.closed === 'undefined') {
          this.$emit(
            'error',
            'El navegador bloqueó la ventana emergente. Usa el botón "Abrir en nueva pestaña".'
          );
        }
        return;
      }

      // HTML directo
      if (this.payHtml) {
        var winHtml = window.open('', 'paysquare_popup_html', features);
        if (!winHtml || winHtml.closed || typeof winHtml.closed === 'undefined') {
          this.$emit(
            'error',
            'El navegador bloqueó la ventana emergente. Usa el botón "Abrir en nueva pestaña".'
          );
          return;
        }
        try {
          winHtml.document.open();
          winHtml.document.write(this.payHtml);
          winHtml.document.close();
        } catch (err) {
          console.error('openPopup HTML failed', err);
          this.$emit('error', 'No se pudo abrir la ventana con el HTML del checkout');
        }
      }
    },
  },
};
</script>

<style scoped>
/* ===== Paleta clara minimalista ===== */
:root {
  --pc-bg: #ffffff;
  --pc-surface: #ffffff;
  --pc-border: #dce1ec;
  --pc-text: #0f172a;
  --pc-muted: #6b7280;
  --pc-primary: #0ea5e9;
  --pc-ring: rgba(14, 165, 233, 0.25);
}

/* Card */
.pc-card {
  background: var(--pc-bg);
  color: var(--pc-text);
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(2, 6, 23, 0.18);
  overflow: hidden;
}

/* Header */
.pc-header {
  position: relative;
  padding: 18px 20px 6px;
  background: linear-gradient(180deg, #ffffff, #f7f9fd);
  border-bottom: 1px solid #eef2f7;
}

.pc-title {
  margin: 10px 0 4px;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: #0f172a;
}

.pc-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  background: #ecfeff;
  border: 1px solid #cffafe;
  padding: 6px 10px;
  border-radius: 999px;
}

.pc-close {
  position: absolute;
  right: 6px;
  top: 6px;
  color: #6b7280;
}

/* Body */
.pc-body {
  padding: 18px 18px 10px;
}

/* Form */
.pc-form {
  background: var(--pc-surface);
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px;
}

.pc-input :deep(.v-input__slot) {
  border-radius: 12px !important;
  min-height: 48px !important;
  background: #ffffff !important;
  border: 1px solid var(--pc-border) !important;
}

/* Monto */
.pc-amount {
  border: 1.5px solid var(--pc-primary);
  border-radius: 14px;
  padding: 12px;
  margin-bottom: 14px;
  box-shadow: 0 0 0 4px var(--pc-ring);
  background: #ffffff;
}

.pc-amount__label {
  display: block;
  margin-bottom: 8px;
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
}

.pc-amount__input :deep(.v-input__slot) {
  border-radius: 12px !important;
  min-height: 54px !important;
  background: #ffffff;
  border: 1px solid var(--pc-border) !important;
}

.pc-amount__input :deep(input) {
  font-size: 22px !important;
  font-weight: 800 !important;
  letter-spacing: 0.5px;
  color: #0f172a !important;
}

.pc-amount__hint {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: var(--pc-muted);
}

/* ✅ Fee info */
.pc-fee {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e5e7eb;
}

.pc-fee__line {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: #6b7280;
  font-size: 12px;
}

.pc-fee__total {
  margin-top: 4px;
  color: #0f172a;
  font-weight: 700;
}

/* CTA */
.pc-cta {
  margin-top: 14px;
  width: 100%;
  color: #fff !important;
  background: #0ea5e9 !important;
  text-transform: none;
  font-weight: 800;
  letter-spacing: 0.2px;
  border-radius: 12px;
  height: 46px;
  box-shadow: 0 10px 18px rgba(14, 165, 233, 0.18);
}

/* Contenedor paso 2 */
.pc-iframe-wrap {
  margin-top: 8px;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  overflow: hidden;
}

/* Footer */
.pc-actions {
  padding: 10px 14px 16px;
  border-top: 1px solid #eef2f7;
}

.pc-secondary {
  color: #6b7280 !important;
  text-transform: none;
}

.pc-actions-inline {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

/* Loading */
.pc-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #6b7280;
  font-size: 13px;
  margin: 10px 0;
}

.pc-spinner {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid #e5e7eb;
  border-top-color: var(--pc-primary);
  animation: pc-spin 1s linear infinite;
}

@keyframes pc-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Launcher */
.pc-launcher {
  padding: 0;
  min-height: auto;
  box-shadow: none;
}

.pc-launcher:hover {
  transform: scale(1.02);
}

/* Forzar fondo blanco del popup en móviles */
.pc-dialog .v-card,
.pc-dialog .v-card > * {
  background-color: #ffffff !important;
}

.v-overlay__scrim {
  background-color: rgba(11, 18, 32, 0.85) !important;
}

@supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
  .v-overlay__scrim {
    -webkit-backdrop-filter: none !important;
    backdrop-filter: none !important;
  }
}

.theme--dark .pc-dialog .v-card,
.theme--dark .pc-dialog .v-card > * {
  background-color: #ffffff !important;
  color: #0f172a !important;
}

/* ===== Gate básico ===== */
.pp-gate {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 20;
}

.pp-gate__box {
  width: 100%;
  max-width: 420px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 16px 40px rgba(2, 6, 23, 0.12);
  padding: 16px;
}

.pp-gate__title {
  font-weight: 800;
  margin-top: 8px;
  color: #0f172a;
}

.pp-gate__msg {
  margin-top: 6px;
  color: #6b7280;
  font-size: 13px;
}

.pp-gate__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}

/* Ajustes móviles */
@media (max-width: 360px) {
  .pc-body {
    padding: 14px;
  }
  .pc-title {
    font-size: 18px;
  }
}
</style>
